<template>
    <div class="master-template-library">
        <Spin v-if="isLoading && inModal" fix></Spin>
        <template v-else>
            <div class="master-template-library__header" :class="{ 'no-templates': !hasMasterTemplates }">
                <SectionNav />

                <MasterTemplatesSortBy @input="setSort" />
                <MasterTemplateTextFilter />
                <MasterTemplateSelectViewControl />

                <MultiviewSelectAllControl
                    v-if="isSelectAllVisible"
                    class="master-template-library__select-all"
                    :show-empty="false"
                    instance-name="masterTemplates"
                />

                <MasterTemplateUploadButton
                    v-if="userCanManageMasterTemplates && !selected.length"
                    class="master-template-library__upload"
                    label="Add template"
                    data-testid="upload-template__button"
                />

                <Button
                    v-if="userCanManageMasterTemplates && selected.length"
                    type="primary"
                    class="master-template-library__upload"
                    @click="$emit('add')"
                >
                    Add to campaign
                </Button>
            </div>

            <MultiviewSelectAllNotice instance-name="masterTemplates" />

            <MasterTemplateUploadList v-model:file-list="recentlyUploadedFiles" />

            <hox-empty-message
                v-if="masterTemplateLibraryFilters && !hasMasterTemplates"
                class="master-template-library__no-matches"
            >
                <template #title>
                    No <span v-if="campaignType">{{ TemplateTypeLabel[campaignType] }}</span> templates to show
                </template>
                <template #content>
                    <p>
                        Please check your filter selection or upload a new
                        <span v-if="campaignType">{{ TemplateTypeLabel[campaignType] }}</span>
                        template by clicking "Add template"
                    </p>
                </template>
            </hox-empty-message>

            <MasterTemplateSelect
                v-if="hasMasterTemplates || !masterTemplateLibraryFilters"
                class="master-template-library__template-select"
                :items="masterTemplateIds"
                :campaign-template-ids="campaignMasterTemplateIds"
                :sort-by="sortBy"
                instance-name="masterTemplates"
                @select="onSelect"
            />
        </template>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { mapStores } from "pinia";

import { USER_ACTIONS } from "shared-utils/enums/user";

import bus from "@/bus";
import clientMasterTemplatesQuery from "@/apollo/queries/v2/ClientMasterTemplates.gql";

import { UiAction } from "@/store/modules/ui";
import {
    MasterTemplateEvents,
    TemplatesSortBy,
    TemplateTypeLabel,
    type TemplateSortByType,
    TemplateType
} from "@/enums/masterTemplate";
import type { MasterTemplateFile } from "@/types/types";

import MasterTemplateUploadButton from "@/components/Campaign/MasterTemplates/MasterTemplateUploadButton.vue";
import MasterTemplateUploadList from "@/components/Campaign/MasterTemplates/MasterTemplateUploadList.vue";
import MasterTemplateSelect from "@/components/Campaign/MasterTemplateSelect.vue";
import MasterTemplatesSortBy from "@/components/Campaign/MasterTemplatesSortBy.vue";
import MasterTemplateTextFilter from "@/components/Campaign/MasterTemplates/MasterTemplateTextFilter.vue";
import MasterTemplateSelectViewControl from "@/components/Campaign/MasterTemplateSelectViewControl.vue";
import MultiviewSelectAllControl from "@/components/Campaign/MultiviewSelectAllControl.vue";
import MultiviewSelectAllNotice from "@/components/Campaign/MultiviewSelectAllNotice.vue";
import SectionNav from "@/components/SectionNav.vue";
import { useMasterTemplates } from "@/composables/useMasterTemplates";
import { useMasterTemplatesLibraryStore } from "@/store/pinia/masterTemplatesLibrary";
import { userCan } from "@/services/Auth";

export default defineComponent({
    name: "MasterTemplateLibrary",

    components: {
        MasterTemplateSelectViewControl,
        MasterTemplateTextFilter,
        MasterTemplatesSortBy,
        MultiviewSelectAllNotice,
        MultiviewSelectAllControl,
        MasterTemplateSelect,
        MasterTemplateUploadButton,
        MasterTemplateUploadList,
        SectionNav
    },

    props: {
        campaignMasterTemplateIds: {
            type: [Array, null] as PropType<string[] | null>,
            default() {
                return null;
            }
        },
        inModal: {
            type: Boolean,
            default: false
        }
    },

    emits: ["add", "select"],

    setup() {
        const { masterTemplatesNumberByType } = useMasterTemplates();

        return {
            masterTemplatesNumberByType
        };
    },

    data() {
        return {
            TemplateTypeLabel,
            isLoading: true,
            selected: [] as string[],
            sortBy: TemplatesSortBy.WIDTH_ASC as TemplateSortByType,
            updateUploadListHandler: null as null | ((template: MasterTemplateFile) => void),
            refreshListHandler: null as null | (() => void)
        };
    },

    computed: {
        ...mapStores(useMasterTemplatesLibraryStore),

        userCanListMasterTemplates() {
            return userCan(USER_ACTIONS.canListMasterTemplates, { clientId: this.clientId }, false);
        },

        userCanManageMasterTemplates() {
            return userCan(USER_ACTIONS.canManageMasterTemplates, { clientId: this.clientId }, false);
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        campaignType() {
            return this.$store.state.campaign.campaignType as keyof typeof TemplateType;
        },

        hasMasterTemplates() {
            if (this.campaignType) {
                const templatesNumber = this.masterTemplatesNumberByType[this.campaignType];

                return !!templatesNumber;
            }

            return !!this.masterTemplateIds.length;
        },

        isPermissionGranted() {
            return this.whoAmI && this.userCanListMasterTemplates;
        },

        isSelectAllVisible() {
            return this.campaignMasterTemplateIds !== null;
        },

        masterTemplateIds() {
            return this.masterTemplatesLibraryStore.getFilteredMasterTemplateIds;
        },

        recentlyUploadedFiles: {
            get() {
                return this.masterTemplatesLibraryStore.recentlyUploadedFiles;
            },

            set(value: Record<string, MasterTemplateFile>) {
                if (Object.keys(value).length === 0) {
                    this.masterTemplatesLibraryStore.clearRecentlyUploadedFiles();
                }
            }
        },

        masterTemplateLibraryFilters() {
            const hasSizeFilter = this.masterTemplatesLibraryStore.sizes.length > 0;
            const hasTypeFilter = this.masterTemplatesLibraryStore.types.length > 0;
            const hasSearchFilter = !!this.masterTemplatesLibraryStore.search;
            const hasFamilyFilter = this.masterTemplatesLibraryStore.families.length > 0;
            const hasHideResizedFilter = !!this.masterTemplatesLibraryStore.hideResized;

            return hasSizeFilter || hasTypeFilter || hasSearchFilter || hasFamilyFilter || hasHideResizedFilter;
        },

        showSidebar() {
            return this.hasMasterTemplates || this.masterTemplateLibraryFilters;
        },

        whoAmI() {
            return this.$store.state.auth.me;
        }
    },

    watch: {
        showSidebar: {
            immediate: true,
            handler(val) {
                if (!this.inModal) {
                    this.$store.dispatch(val ? UiAction.OpenSidebar : UiAction.CloseSidebar);
                }
            }
        }
    },

    created() {
        this.updateUploadListHandler = (template: MasterTemplateFile) => {
            this.masterTemplatesLibraryStore.updateRecentlyUploadedFile(template);
        };

        this.refreshListHandler = () => {
            void this.onUpdate();
        };

        bus.$on(MasterTemplateEvents.UpdateMasterTemplateUploadList, this.updateUploadListHandler);
        bus.$on(MasterTemplateEvents.RefreshMasterTemplateList, this.refreshListHandler);
    },

    beforeUnmount() {
        if (this.updateUploadListHandler) {
            bus.$off(MasterTemplateEvents.UpdateMasterTemplateUploadList, this.updateUploadListHandler);
        }

        if (this.refreshListHandler) {
            bus.$off(MasterTemplateEvents.RefreshMasterTemplateList, this.refreshListHandler);
        }
    },

    unmounted() {
        this.masterTemplatesLibraryStore.clearState();
    },

    methods: {
        onSelect(selected: string[]) {
            this.selected = selected;
            this.$emit("select", selected);
        },

        async onUpdate() {
            await this.$apollo.queries.masterTemplates?.refetch();
        },

        setSort(sortBy: TemplateSortByType) {
            this.sortBy = sortBy;
        }
    },

    apollo: {
        masterTemplates: {
            query: clientMasterTemplatesQuery,
            variables() {
                return {
                    clientId: this.clientId
                };
            },
            manual: true,
            skip() {
                return !this.isPermissionGranted;
            },
            result({ data }) {
                this.masterTemplatesLibraryStore.setMasterTemplates(data.masterTemplatesByClient);
                this.masterTemplatesLibraryStore.syncFilters(this.campaignMasterTemplateIds);

                this.isLoading = false;
            },

            error(e) {
                bus.$emit("apolloErrorEvent", e as Error);
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.master-template-library {
    display: flex;
    flex-direction: column;
}

.master-template-library__filter-tags {
    flex: 0 0 auto;
    margin: 22px 0;
    padding: 0 20px;

    &.filterTags--empty {
        margin: 0;
        padding: 0;
    }
}

.master-template-library__no-matches {
    margin-top: $spacing;
}

.master-template-library__header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex: 0 0 auto;
    padding: 0 20px;
    border-bottom: 1px solid $grey3;
    height: 60px;
    margin: -30px 0 0;

    &.no-templates {
        .multiview-select-all {
            visibility: hidden;
        }
    }
}

.master-template-library__select-all {
    margin-right: $spacing-semi-small;
}

.master-template-library__template-select {
    flex: 1;
}

.templates-modal .master-template-library {
    &__header {
        margin-top: 0;
        border-bottom: 0;
    }
}
</style>
